import { defineMessages } from "react-intl"
import { ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import GlobalHelper from "../../../../common/GlobalHelper"
import { PAYMENT_3DS_STATUS, PAYMENT_STATUS } from "../../../helpers/Payment"
import { INTEGRATION_KEY } from "../../../integration/Constants"
import { PREFERENCE_TYPE } from "../../constants"
    
export enum PaymentsColumnIds {
    IDENTIFIER = "identifier",
    PAYMENT_TYPE = "paymentTypeName",
    CAPTURED_AT_DATE = "capturedAt",
    PAYMENT_STATUS = "paymentStatus",
    PAYMENT_AMOUNT = "paymentAmount",
    NOTES = "notes",
    PAYMENT_SOURCE = "paymentSource",
    PAYMENT_CURRENCY = "paymentCurrency",
    VOIDED_AT_DATE = "voidedAt",
    INTEGRATION = "integrationKey",
    CONTACT = "contact",
    CONTACT_NAME = "contactName",
    CONTACT_FIRST_NAME = "contactFirstName",
    CONTACT_LAST_NAME = "contactLastName",
    CONTACT_ORGANIZATION_NAME = "contactOrganizationName",
    CONTACT_TYPE = "contactType",
    CONTACT_PHONE_NUMBER = "contactPhoneNumber",
    CONTACT_TAX_NUMBER = "contactTaxNumber",
    CONTACT_EMAIL = "contactEmail",
    DATA_3DS_STATUS = "data3DSStatus",
    DATA_3DS_REQUESTED = "data3DSRequested",
    DATA_3DS_FAILED_REASON = "data3DSFailedReason",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: PaymentsColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/payments/{id}/edit"
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PaymentsColumnIds.PAYMENT_TYPE,
        sortId: "paymentTypeId",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: PaymentsColumnIds.CAPTURED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PaymentsColumnIds.PAYMENT_STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                [PAYMENT_STATUS.PENDING]: {
                    message: {
                        id: "generic-listing.payments.badge.pending",
                        defaultMessage: "Pending",
                    },
                    type: "warning",
                    icon: "hourglass-half"
                },
                [PAYMENT_STATUS.CAPTURED]: {
                    message: {
                        id: "generic-listing.payments.badge.captured",
                        defaultMessage: "Captured",
                    },
                    type: "success",
                    icon: "check"
                },
                [PAYMENT_STATUS.PAID]: {
                    message: {
                        id: "generic-listing.payments.badge.paid",
                        defaultMessage: "Paid",
                    },
                    type: "success",
                    icon: "check"
                },
                [PAYMENT_STATUS.DECLINED]: {
                    message: {
                        id: "generic-listing.payments.badge.declined",
                        defaultMessage: "Declined",
                    },
                    type: "danger",
                    icon: "times"
                },
                [PAYMENT_STATUS.EXPIRED]: {
                    message: {
                        id: "generic-listing.payments.badge.expired",
                        defaultMessage: "Expired",
                    },
                    type: "danger",
                    icon: "times"
                },
                [PAYMENT_STATUS.VOIDED]: {
                    message: {
                        id: "generic-listing.payments.badge.voided",
                        defaultMessage: "Voided",
                    },
                    type: "danger",
                    icon: "times-circle"
                },
                [PAYMENT_STATUS.SYNC_REQUESTED]: {
                    message: {
                        id: "generic-listing.payments.badge.sync-requested",
                        defaultMessage: "Sync requested",
                    },
                    type: "warning",
                    icon: "hourglass-half"
                },
                [PAYMENT_STATUS.REFUNDED]: {
                    message: {
                        id: "generic-listing.payments.badge.refunded",
                        defaultMessage: "Refunded",
                    },
                    type: "warning",
                    icon: "undo"
                },
                [PAYMENT_STATUS.PARTIALLY_REFUNDED]: {
                    message: {
                        id: "generic-listing.payments.badge.partially-refunded",
                        defaultMessage: "Partially refunded",
                    },
                    type: "warning",
                    icon: "undo"
                },
                [PAYMENT_STATUS.TRANSFERRED]: {
                    message: {
                        id: "generic-listing.payments.badge.transferred",
                        defaultMessage: "Transferred",
                    },
                    type: "warning",
                    icon: "share"
                },
                [PAYMENT_STATUS.CANCELED]: {
                    message: {
                        id: "generic-listing.payments.badge.canceled",
                        defaultMessage: "Canceled",
                    },
                    type: "secondary",
                    icon: "times"
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PaymentsColumnIds.PAYMENT_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.paymentAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: PaymentsColumnIds.NOTES,
        dataType: DataType.STRING,
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PaymentsColumnIds.PAYMENT_SOURCE,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: PaymentsColumnIds.PAYMENT_CURRENCY,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: PaymentsColumnIds.VOIDED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PaymentsColumnIds.INTEGRATION,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                [INTEGRATION_KEY.RAINFOREST]: {
                    id: "generic-listing.payment-integration.rainforest",
                    defaultMessage: "QuoteMachine Payments",
                },
                [INTEGRATION_KEY.STRIPE]: {
                    id: "generic-listing.payment-integration.stripe",
                    defaultMessage: "Stripe",
                },
                [INTEGRATION_KEY.SQUARE_PAYMENTS]: {
                    id: "generic-listing.payment-integration.square-payments",
                    defaultMessage: "Square Payments",
                },
                [INTEGRATION_KEY.SQUARE]: {
                    id: "generic-listing.payment-integration.square",
                    defaultMessage: "Square",
                },
                [INTEGRATION_KEY.PAYSAFE]: {
                    id: "generic-listing.payment-integration.paysafe",
                    defaultMessage: "Paysafe",
                },
            },
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: PaymentsColumnIds.CONTACT,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.CONTACT,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_NAME,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_FIRST_NAME,
        sortId: "contact.firstNameKeyword",
        accessorFn: row => row.contact?.firstName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_LAST_NAME,
        sortId: "contact.lastNameKeyword",
        accessorFn: row => row.contact?.lastName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_ORGANIZATION_NAME,
        sortId: "contact.organizationNameSort",
        accessorFn: row => row.contact?.organizationName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_TAX_NUMBER,
        sortId: "contact.taxNumber",
        accessorFn: row => row.contact?.taxNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.CONTACT_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
        hidden: GlobalHelper.isEnvProduction(),
    },
    {
        id: PaymentsColumnIds.DATA_3DS_STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                [PAYMENT_3DS_STATUS.SUCCEEDED]: {
                    message: {
                        id: "generic-listing.payments.badge.3ds-succeeded",
                        defaultMessage: "Succeeded",
                    },
                    type: "success",
                    icon: "check"
                },
                [PAYMENT_3DS_STATUS.FAILED]: {
                    message: {
                        id: "generic-listing.payments.badge.3ds-failed",
                        defaultMessage: "Failed",
                    },
                    type: "danger",
                    icon: "times"
                },
            },
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: PaymentsColumnIds.DATA_3DS_REQUESTED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: PaymentsColumnIds.DATA_3DS_FAILED_REASON,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
]

const headerMessages = defineMessages({
    [PaymentsColumnIds.IDENTIFIER]: {
        id: "generic-listing.payments.table-header.identifier",
        defaultMessage: "ID",
    },
    [PaymentsColumnIds.PAYMENT_TYPE]: {
        id: "generic-listing.payments.table-header.payment-type",
        defaultMessage: "Payment type",
    },
    [PaymentsColumnIds.CAPTURED_AT_DATE]: {
        id: "generic-listing.payments.table-header.captured-at-date",
        defaultMessage: "Payment date",
    },
    [PaymentsColumnIds.PAYMENT_STATUS]: {
        id: "generic-listing.payments.table-header.payment-status",
        defaultMessage: "Status",
    },
    [PaymentsColumnIds.PAYMENT_AMOUNT]: {
        id: "generic-listing.payments.table-header.payment-amount",
        defaultMessage: "Amount",
    },
    [PaymentsColumnIds.NOTES]: {
        id: "generic-listing.payments.table-header.notes",
        defaultMessage: "Notes",
    },
    [PaymentsColumnIds.PAYMENT_SOURCE]: {
        id: "generic-listing.payments.table-header.payment-source",
        defaultMessage: "Payment source",
    },
    [PaymentsColumnIds.PAYMENT_CURRENCY]: {
        id: "generic-listing.payments.table-header.payment-currency",
        defaultMessage: "Currency",
    },
    [PaymentsColumnIds.VOIDED_AT_DATE]: {
        id: "generic-listing.payments.table-header.voided-at-date",
        defaultMessage: "Voided date",
    },
    [PaymentsColumnIds.INTEGRATION]: {
        id: "generic-listing.payments.table-header.integration",
        defaultMessage: "Integration",
    },
    [PaymentsColumnIds.CONTACT]: {
        id: "generic-listing.payments.table-header.contact",
        defaultMessage: "Contact",
    },
    [PaymentsColumnIds.CONTACT_NAME]: {
        id: "generic-listing.payments.table-header.contact-name",
        defaultMessage: "Contact Name",
    },
    [PaymentsColumnIds.CONTACT_FIRST_NAME]: {
        id: "generic-listing.payments.table-header.contact-first-name",
        defaultMessage: "Contact First Name",
    },
    [PaymentsColumnIds.CONTACT_LAST_NAME]: {
        id: "generic-listing.payments.table-header.contact-last-name",
        defaultMessage: "Contact Last Name",
    },
    [PaymentsColumnIds.CONTACT_ORGANIZATION_NAME]: {
        id: "generic-listing.payments.table-header.contact-organization-name",
        defaultMessage: "Contact Organization",
    },
    [PaymentsColumnIds.CONTACT_TYPE]: {
        id: "generic-listing.payments.table-header.contact-type",
        defaultMessage: "Contact Type",
    },
    [PaymentsColumnIds.CONTACT_PHONE_NUMBER]: {
        id: "generic-listing.payments.table-header.contact-phone_number",
        defaultMessage: "Contact Phone Number",
    },
    [PaymentsColumnIds.CONTACT_TAX_NUMBER]: {
        id: "generic-listing.payments.table-header.contact-tax-number",
        defaultMessage: "Contact Tax Number",
    },
    [PaymentsColumnIds.CONTACT_EMAIL]: {
        id: "generic-listing.payments.table-header.contact-email",
        defaultMessage: "Contact Email",
    },
    [PaymentsColumnIds.DATA_3DS_STATUS]: {
        id: "generic-listing.payments.table-header.three-ds-status",
        defaultMessage: "3DS Status",
    },
    [PaymentsColumnIds.DATA_3DS_REQUESTED]: {
        id: "generic-listing.payments.table-header.three-ds-requested",
        defaultMessage: "3DS Requested",
    },
    [PaymentsColumnIds.DATA_3DS_FAILED_REASON]: {
        id: "generic-listing.payments.table-header.three-ds-failed-reason",
        defaultMessage: "3DS Failed Reason",
    },
})

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.PAYMENTS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_PAYMENTS,
    searchPlaceholder: {
        id: "generic-listing.payments.search-placeholder",
        defaultMessage: "Search a payment...",
    },
    displayString: {
        id: "generic-listing.payments.display-string",
        defaultMessage: "Payments",
    },
    searchObjectType: ObjectType.PAYMENTS,
    bulkActionDefinitions: [],
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/payments.ts